import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { NavIcon, IconSun, IconMoon, IconLogout } from '../components/Icons';

const NAV_ITEMS = [
  { to: '/', label: 'Продажи' },
  { to: '/marketing', label: 'Маркетинг' },
  { to: '/education', label: 'Обучение' },
  { to: '/amo-1c', label: 'АМОИ1С' },
  { to: '/admin/data', label: 'Данные', adminOnly: true },
  { to: '/admin/users', label: 'Пользователи', adminOnly: true },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, mode, toggleMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{ ...styles.container, background: 'var(--bg)' }}>
      <aside
        style={{
          ...styles.sidebar,
          width: collapsed ? 64 : 228,
          background: 'linear-gradient(180deg, var(--bg-sidebar) 0%, var(--bg) 100%)',
          borderRight: '1px solid var(--border)',
        }}
      >
        <div style={styles.logoWrap} onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}>
          <img
            src={mode === 'light' ? '/dasna_logo_black.png' : '/dasna_logo_white.png'}
            alt="DASNA"
            style={{ maxWidth: collapsed ? 40 : '82%', height: 'auto', objectFit: 'contain', transition: 'transform .25s cubic-bezier(0.22,1,0.36,1)' }}
          />
        </div>

        <div style={{ ...styles.divider, background: 'linear-gradient(90deg, transparent, var(--border), transparent)' }} />

        <nav style={styles.nav}>
          {NAV_ITEMS.filter((item) => !item.adminOnly || user?.role === 'admin').map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                isActive ? 'nav-dash-link nav-dash-link--active' : 'nav-dash-link'
              }
              title={collapsed ? item.label : undefined}
            >
              <NavIcon to={item.to} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {!collapsed && (
          <div style={{ ...styles.dataFreshness, color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
            Данные актуальны на 30.09.26
          </div>
        )}

        <div style={{ ...styles.sidebarFooter, borderTop: '1px solid var(--border)' }}>
          <button
            type="button"
            onClick={toggleMode}
            title={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            style={{
              ...styles.iconBtn,
              color: 'var(--text-secondary)',
              borderColor: 'var(--border)',
              background: 'transparent',
            }}
          >
            {mode === 'dark' ? <IconSun /> : <IconMoon />}
          </button>

          {!collapsed ? (
            <div style={styles.userBlock}>
              <div style={styles.avatar}>{(user?.username || 'U')[0].toUpperCase()}</div>
              <div style={styles.userInfo}>
                <span style={{ ...styles.username, color: 'var(--text-primary)' }}>{user?.username}</span>
                <span style={{ ...styles.userRole, color: 'var(--text-muted)' }}>
                  {user?.role === 'admin' ? 'Администратор' : 'Пользователь'}
                </span>
              </div>
            </div>
          ) : (
            <div style={styles.avatar}>{(user?.username || 'U')[0].toUpperCase()}</div>
          )}

          <button
            type="button"
            onClick={handleLogout}
            title="Выйти"
            style={{
              ...styles.iconBtn,
              color: 'var(--text-muted)',
              borderColor: 'var(--border)',
              background: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(248,113,113,.12)';
              e.currentTarget.style.color = 'var(--danger)';
              e.currentTarget.style.borderColor = 'rgba(248,113,113,.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-muted)';
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            {collapsed ? <IconLogout /> : <span style={{ fontSize: 12, fontWeight: 500 }}>Выйти</span>}
          </button>
        </div>
      </aside>
      <main
        style={{
          ...styles.main,
          backgroundImage: 'var(--main-backdrop)',
        }}
        className="dashboard-main-enter"
        key={location.pathname}
      >
        <Outlet />
      </main>
    </div>
  );
}

const styles = {
  container: { display: 'flex', minHeight: '100vh' },
  sidebar: {
    boxShadow: '4px 0 24px rgba(0,0,0,.18)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width .28s cubic-bezier(0.22, 1, 0.36, 1)',
    overflow: 'hidden',
    flexShrink: 0,
  },
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '22px 14px 18px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  divider: {
    height: 1,
    margin: '0 16px',
  },
  nav: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 8px' },
  sidebarFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 10px',
    gap: 8,
    flexWrap: 'wrap',
  },
  userBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    overflow: 'hidden',
    flex: 1,
    minWidth: 0,
  },
  avatar: {
    width: 32, height: 32, borderRadius: 10,
    background: 'linear-gradient(135deg, #22d3ee 0%, #34d399 100%)',
    color: '#0b1220', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, flexShrink: 0,
  },
  userInfo: {
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  username: {
    fontSize: 13, fontWeight: 600,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  userRole: {
    fontSize: 10, fontWeight: 500, marginTop: 1,
  },
  dataFreshness: {
    fontSize: 10, fontWeight: 500,
    textAlign: 'center', padding: '8px 12px',
    letterSpacing: 0.3,
  },
  iconBtn: {
    border: '1px solid',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '6px 10px',
    minHeight: 32,
    transition: 'all .2s',
    flexShrink: 0,
  },
  main: {
    flex: 1,
    overflow: 'auto',
    padding: '28px 32px',
    position: 'relative',
  },
};
